import React, { useState, useEffect, useCallback } from "react";
import PageGuide from "../../components/PageGuide";
import { prettyStatus } from "../../components/PaperForm/styles";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Container, Stack, Typography, Breadcrumbs, Box, Paper,
  TableHead, TableBody, TableRow, TableCell, TableContainer,
  Button, Chip, CircularProgress, Alert, Select, MenuItem,
  FormControl, InputLabel, Tooltip,
} from "@mui/material";
import {
  HomeRounded, ArrowBackRounded, DownloadRounded,
  CheckCircle, Cancel, VerifiedOutlined, LinkOff,
} from "@mui/icons-material";
import ApiCall from "../../utils/apicall";

import SmartTable from "../../components/SmartTable/SmartTable";
// ─── helpers ─────────────────────────────────────────────────────────────────

const HEAT_STATUS_COLOR = {
  quarantine: "warning",
  released:   "success",
  consumed:   "info",
};

function exportCSV(jobName, heats) {
  const rows = [
    [
      "Heat Number", "Grade", "Shape", "Supplier",
      "Received Qty", "Unit", "Received Date",
      "Heat Status", "CMTR Verified", "CMTR Verified Date",
      "MTC File", "Linked Piece Count", "Linked Pieces",
    ],
  ];
  for (const h of heats) {
    rows.push([
      h.heatNumber,
      h.grade || "",
      h.shape || "",
      h.supplier || "",
      h.quantity || "",
      h.unit || "",
      h.receivedDate ? new Date(h.receivedDate).toLocaleDateString() : "",
      h.status,
      h.cmtrVerified ? "Yes" : "No",
      h.cmtrVerifiedAt ? new Date(h.cmtrVerifiedAt).toLocaleDateString() : "",
      h.mtcFilename || "",
      h.linked_piece_count,
      h.linked_pieces.join(", "),
    ]);
  }
  const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `AssemblyHeatReport_${jobName || "Job"}_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

// ─── main component ──────────────────────────────────────────────────────────

export default function AssemblyHeatReport() {
  const navigate = useNavigate();
  const [jobs, setJobs]       = useState([]);
  const [jobId, setJobId]     = useState("");
  const [jobName, setJobName] = useState("");
  const [heats, setHeats]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    ApiCall.get("/job").then((r) => setJobs(r.data?.data?.jobs || [])).catch(() => {});
  }, []);

  const runReport = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setHeats([]);
    try {
      const r = await ApiCall.get("/reports/assembly-heat", { params: { jobId: id } });
      setHeats(r.data.data.heats || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleJobChange = (e) => {
    const id = e.target.value;
    setJobId(id);
    const job = jobs.find((j) => j.id === id || j.id === parseInt(id));
    setJobName(job?.name || "");
    runReport(id);
  };

  const allCmtrVerified = heats.length > 0 && heats.every((h) => h.cmtrVerified);

  return (
    <>
      <Helmet><title>Assembly Heat Report | SFM</title></Helmet>
      <Container maxWidth="xl">

        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Breadcrumbs>
            <Stack direction="row" alignItems="center" spacing={1}>
              <HomeRounded color="inherit" fontSize="small" />
              <Typography
                variant="body2" sx={{ cursor: "pointer" }}
                onClick={() => navigate("/reports")}
              >
                Reports
              </Typography>
            </Stack>
            <Typography variant="body2" color="textPrimary">Assembly Heat Report</Typography>
          </Breadcrumbs>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined" size="small"
              startIcon={<ArrowBackRounded />}
              onClick={() => navigate("/reports")}
            >
              Back
            </Button>
            {heats.length > 0 && (
              <Button
                variant="contained" size="small"
                startIcon={<DownloadRounded />}
                onClick={() => exportCSV(jobName, heats)}
              >
                Export CSV
              </Button>
            )}
          </Stack>
        </Stack>
        <PageGuide
          why="Show the material heats behind each assembly — full piece-to-material traceability."
          who="QC and the QM, proving assembly-to-material traceability."
          what="A report linking assemblies to the heats used in them."
          how="Filter by job, then export."
          when="For traceability reviews and customer reporting."
          standard="AISC §2.5 material traceability"
        />

        <Typography variant="h5" fontWeight={700} mb={0.5}>Assembly Heat Report</Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          All heats contributing material to this assembly — use for MTR submittal packages and inspector review.
        </Typography>

        {/* Job selector */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControl sx={{ minWidth: 320 }} size="small">
            <InputLabel>Select Job</InputLabel>
            <Select value={jobId} label="Select Job" onChange={handleJobChange}>
              {jobs.map((j) => (
                <MenuItem key={j.id} value={j.id}>{j.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>

        {loading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* CMTR summary banner */}
        {!loading && heats.length > 0 && (
          <Alert
            severity={allCmtrVerified ? "success" : "warning"}
            icon={allCmtrVerified ? <VerifiedOutlined /> : undefined}
            sx={{ mb: 2 }}
          >
            {allCmtrVerified
              ? `All ${heats.length} heat${heats.length !== 1 ? "s" : ""} have verified CMTRs — package is MTR-ready.`
              : `${heats.filter((h) => !h.cmtrVerified).length} of ${heats.length} heats have unverified CMTRs — resolve before submittal.`}
          </Alert>
        )}

        {/* Stats */}
        {!loading && heats.length > 0 && (
          <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
            <Chip label={`${heats.length} Heat${heats.length !== 1 ? "s" : ""}`} variant="outlined" size="small" />
            <Chip
              label={`${heats.filter((h) => h.cmtrVerified).length} CMTR Verified`}
              color={allCmtrVerified ? "success" : "warning"} size="small"
            />
            <Chip
              label={`${heats.filter((h) => h.mtcFilename).length} MTC on File`}
              color="info" size="small" variant="outlined"
            />
            <Chip
              label={`${heats.reduce((s, h) => s + parseInt(h.linked_piece_count || 0), 0)} Pieces Covered`}
              color="default" size="small" variant="outlined"
            />
          </Stack>
        )}

        {/* Table */}
        {!loading && heats.length > 0 && (
          <Paper>
            <TableContainer>
              <SmartTable tableId="report-assembly-heat" lockedColumns={["Heat Number", "Status", "CMTR"]}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Heat Number</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Shape</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Supplier</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Qty / Unit</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>CMTR</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>MTC</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Pieces Linked</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Piece Marks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {heats.map((h) => (
                    <TableRow key={h.id} hover sx={{ bgcolor: !h.cmtrVerified ? "rgba(255,152,0,0.04)" : "inherit" }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>{h.heatNumber}</Typography>
                        {h.receivedDate && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Rcvd {new Date(h.receivedDate).toLocaleDateString()}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{h.grade || "—"}</TableCell>
                      <TableCell>{h.shape || "—"}</TableCell>
                      <TableCell>{h.supplier || "—"}</TableCell>
                      <TableCell>
                        {h.quantity ? `${h.quantity} ${h.unit || "lbs"}` : "—"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={prettyStatus(h.status)}
                          size="small"
                          color={HEAT_STATUS_COLOR[h.status] || "default"}
                        />
                      </TableCell>
                      <TableCell>
                        {h.cmtrVerified ? (
                          <Tooltip title={h.cmtrVerifiedAt ? `Verified ${new Date(h.cmtrVerifiedAt).toLocaleDateString()}` : "Verified"}>
                            <CheckCircle color="success" sx={{ fontSize: 20 }} />
                          </Tooltip>
                        ) : (
                          <Tooltip title="CMTR not yet verified">
                            <Cancel color="error" sx={{ fontSize: 20 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        {h.mtcFilename ? (
                          <Chip label="On file" size="small" color="info" variant="outlined" />
                        ) : (
                          <Chip label="Missing" size="small" color="warning" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={h.linked_piece_count} size="small" />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "normal", lineHeight: 1.6 }}>
                          {h.linked_pieces.join(", ") || "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </SmartTable>
            </TableContainer>
          </Paper>
        )}

        {!loading && !error && jobId && heats.length === 0 && (
          <Paper sx={{ p: 5, textAlign: "center" }}>
            <LinkOff sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
            <Typography color="text.secondary">
              No heats linked to fabricated items for this job yet.
              Link heats via Heat Management → Heat Detail → Link Piece.
            </Typography>
          </Paper>
        )}

        {!loading && !jobId && (
          <Paper sx={{ p: 5, textAlign: "center" }}>
            <Typography color="text.secondary">Select a job above to generate the Assembly Heat Report.</Typography>
          </Paper>
        )}

      </Container>
    </>
  );
}
